import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, RefreshCw, ChevronDown, AlertCircle, Database } from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport configurations with multiple data sources
const SPORTS = [
  { id: 'football', name: 'Football (Soccer)', icon: '⚽', endpoint: '/api/scores/football', source: 'API-SPORTS' },
  { id: 'football-data', name: 'Soccer (Europe)', icon: '⚽', endpoint: '/api/scores/soccer/football-data', source: 'football-data.org', hasCompetition: true },
  { id: 'basketball', name: 'Basketball (NBA)', icon: '🏀', endpoint: '/api/scores/nba/balldontlie', source: 'balldontlie.io' },
  { id: 'hockey', name: 'Hockey (NHL)', icon: '🏒', endpoint: '/api/scores/hockey', source: 'API-SPORTS' },
  { id: 'nfl', name: 'American Football (NFL)', icon: '🏈', endpoint: '/api/scores/nfl', source: 'API-SPORTS' },
  { id: 'baseball', name: 'Baseball (MLB)', icon: '⚾', endpoint: '/api/scores/baseball', source: 'API-SPORTS' },
];

// API-SPORTS football leagues
const FOOTBALL_LEAGUES = [
  { id: null, name: 'All Leagues' },
  { id: 39, name: 'Premier League' },
  { id: 140, name: 'La Liga' },
  { id: 135, name: 'Serie A' },
  { id: 78, name: 'Bundesliga' },
  { id: 61, name: 'Ligue 1' },
  { id: 2, name: 'Champions League' },
  { id: 253, name: 'MLS' },
];

// Football-data.org competitions
const FOOTBALL_DATA_COMPETITIONS = [
  { id: 'PL', name: 'Premier League 🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { id: 'PD', name: 'La Liga 🇪🇸' },
  { id: 'SA', name: 'Serie A 🇮🇹' },
  { id: 'BL1', name: 'Bundesliga 🇩🇪' },
  { id: 'FL1', name: 'Ligue 1 🇫🇷' },
  { id: 'CL', name: 'Champions League 🏆' },
  { id: 'DED', name: 'Eredivisie 🇳🇱' },
  { id: 'PPL', name: 'Primeira Liga 🇵🇹' },
];

const ScoresPage = () => {
  const [selectedSport, setSelectedSport] = useState('basketball');
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState('PL');
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to yesterday for better results
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dataSource, setDataSource] = useState('');

  const fetchScores = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const sport = SPORTS.find(s => s.id === selectedSport);
      let url = `${API_URL}${sport.endpoint}?date=${selectedDate}`;
      
      // Add league filter for API-SPORTS football
      if (selectedSport === 'football' && selectedLeague) {
        url += `&league_id=${selectedLeague}`;
      }
      
      // Add competition filter for football-data.org
      if (selectedSport === 'football-data' && selectedCompetition) {
        url += `&competition=${selectedCompetition}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      setScores(data.games || []);
      setDataSource(data.source || sport.source);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      setError('Failed to fetch scores. Please try again.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScores();
  }, [selectedSport, selectedLeague, selectedCompetition, selectedDate]);

  const getWinnerText = (game) => {
    if (game.score.home > game.score.away) return 'Home Win';
    if (game.score.away > game.score.home) return 'Away Win';
    return 'Draw';
  };

  const getWinnerClass = (game, team) => {
    const homeWin = game.score.home > game.score.away;
    const awayWin = game.score.away > game.score.home;
    
    if (team === 'home' && homeWin) return 'text-green-400 font-bold';
    if (team === 'away' && awayWin) return 'text-green-400 font-bold';
    if (game.score.home === game.score.away) return 'text-amber-400';
    return 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-[#050810] py-8">
      <SEO
        title="Final Sports Scores — NFL, NBA, MLB, NHL, Soccer"
        description="Live final scores from NFL, NBA, MLB, NHL, MLS, Premier League, La Liga, Serie A, Bundesliga, Ligue 1, and Champions League — powered by API-SPORTS, balldontlie.io, and football-data.org."
        keywords="final scores, NFL scores, NBA scores, MLB scores, NHL scores, Premier League results, soccer scores, sports results"
        url="https://amppredict.com/scores"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Trophy className="h-8 w-8 text-amber-400" />
            Final Scores
          </h1>
          <p className="text-gray-400 mt-2">
            View final scores from completed games across all major sports
          </p>
        </div>

        {/* Controls */}
        <div className="bg-[#0a1628] rounded-xl border border-[#1e2a45] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Sport Selector */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">Sport</label>
              <div className="relative">
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="w-full bg-[#131b2e] border border-[#1e2a45] rounded-lg px-4 py-2.5 text-white appearance-none cursor-pointer focus:outline-none focus:border-[#3B82F6]"
                  data-testid="sport-selector"
                >
                  {SPORTS.map(sport => (
                    <option key={sport.id} value={sport.id}>
                      {sport.icon} {sport.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* League Selector (API-SPORTS Football only) */}
            {selectedSport === 'football' && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">League</label>
                <div className="relative">
                  <select
                    value={selectedLeague || ''}
                    onChange={(e) => setSelectedLeague(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full bg-[#131b2e] border border-[#1e2a45] rounded-lg px-4 py-2.5 text-white appearance-none cursor-pointer focus:outline-none focus:border-[#3B82F6]"
                    data-testid="league-selector"
                  >
                    {FOOTBALL_LEAGUES.map(league => (
                      <option key={league.id || 'all'} value={league.id || ''}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Competition Selector (football-data.org only) */}
            {selectedSport === 'football-data' && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Competition</label>
                <div className="relative">
                  <select
                    value={selectedCompetition}
                    onChange={(e) => setSelectedCompetition(e.target.value)}
                    className="w-full bg-[#131b2e] border border-[#1e2a45] rounded-lg px-4 py-2.5 text-white appearance-none cursor-pointer focus:outline-none focus:border-[#3B82F6]"
                    data-testid="competition-selector"
                  >
                    {FOOTBALL_DATA_COMPETITIONS.map(comp => (
                      <option key={comp.id} value={comp.id}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Date Picker */}
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-gray-500 mb-1">Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full bg-[#131b2e] border border-[#1e2a45] rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:border-[#3B82F6]"
                  data-testid="date-picker"
                />
              </div>
            </div>

            {/* Refresh Button */}
            <div className="pt-5">
              <Button
                onClick={fetchScores}
                disabled={loading}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                data-testid="refresh-scores"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {(lastUpdated || dataSource) && (
            <div className="flex items-center justify-between mt-3">
              {lastUpdated && (
                <p className="text-xs text-gray-500">
                  Last updated: {lastUpdated}
                </p>
              )}
              {dataSource && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Database className="h-3 w-3" />
                  Source: <span className="text-blue-400">{dataSource}</span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-[#3B82F6] animate-spin" />
          </div>
        )}

        {/* No Games */}
        {!loading && scores.length === 0 && (
          <div className="text-center py-20 bg-[#0a1628] rounded-xl border border-[#1e2a45]">
            <Trophy className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Finished Games</h3>
            <p className="text-gray-400">
              No completed games found for {selectedDate}.
              <br />
              Try selecting a different date or sport.
            </p>
          </div>
        )}

        {/* Scores Grid */}
        {!loading && scores.length > 0 && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              {scores.length} finished game{scores.length !== 1 ? 's' : ''} found
            </p>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {scores.map((game, index) => (
                <div
                  key={game.id || index}
                  className="bg-[#0a1628] rounded-xl border border-[#1e2a45] p-4 hover:border-[#3B82F6]/50 transition-colors"
                  data-testid={`game-card-${index}`}
                >
                  {/* League Info */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500 truncate max-w-[70%]">
                      {game.league?.name || game.league || 'Unknown League'}
                      {game.league?.country && ` • ${game.league.country}`}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      getWinnerText(game) === 'Draw' 
                        ? 'bg-amber-900/30 text-amber-400' 
                        : 'bg-green-900/30 text-green-400'
                    }`}>
                      {getWinnerText(game)}
                    </span>
                  </div>

                  {/* Teams & Score */}
                  <div className="space-y-2">
                    {/* Home Team */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {game.home_team?.logo && (
                          <img 
                            src={game.home_team.logo} 
                            alt="" 
                            className="w-6 h-6 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <span className={`truncate ${getWinnerClass(game, 'home')}`}>
                          {game.home_team?.name || 'Home'}
                        </span>
                      </div>
                      <span className={`text-2xl font-bold ml-2 ${getWinnerClass(game, 'home')}`}>
                        {game.score?.home ?? '-'}
                      </span>
                    </div>

                    {/* Away Team */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {game.away_team?.logo && (
                          <img 
                            src={game.away_team.logo} 
                            alt="" 
                            className="w-6 h-6 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <span className={`truncate ${getWinnerClass(game, 'away')}`}>
                          {game.away_team?.name || 'Away'}
                        </span>
                      </div>
                      <span className={`text-2xl font-bold ml-2 ${getWinnerClass(game, 'away')}`}>
                        {game.score?.away ?? '-'}
                      </span>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="mt-3 pt-3 border-t border-[#1e2a45]">
                    <p className="text-xs text-gray-500">
                      {game.date ? new Date(game.date).toLocaleDateString() : selectedDate}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Note */}
        <div className="mt-8 bg-[#0a1628]/50 rounded-xl border border-[#1e2a45] p-4">
          <p className="text-xs text-gray-500">
            <strong className="text-gray-400">Note:</strong> This page shows final scores from completed games only. 
            No live updates are provided during games. Data is sourced from API-SPORTS.io.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScoresPage;
